import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import {
  useAnnotationDraft,
  useAnnotationStrokeColor,
  useAnnotationTool,
} from '@/features/comment-on-file';
import type { AnnotationShape, AnnotationTool, DraftShape } from '@/features/comment-on-file';
import { useSpaceKeyState } from './useSpaceKeyState';

type DrawingTool = Exclude<AnnotationTool, 'text-highlight'>;

// Minimum fractional distance between successive sampled points for the pen tool.
// ≈ 0.4% of the media — below this, points are dropped to avoid path bloat.
const SAMPLE_DIST = 0.004;
const SAMPLE_DIST_SQ = SAMPLE_DIST * SAMPLE_DIST;

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  /** Which tools should actively capture pointer events. */
  enabledTools?: ReadonlyArray<DrawingTool>;
  /**
   * PDF-only: when provided, every new draft shape is tagged with this page.
   * Image/video flows omit this and store shapes without a page.
   */
  page?: number;
}

export function useShapeDrawing({
  svgRef,
  enabledTools = ['circle', 'rectangle', 'arrow', 'pen'],
  page,
}: Options) {
  const { tool } = useAnnotationTool();
  const { draftShapes, setDraftShapes, cancelDraft } = useAnnotationDraft();
  const { strokeColor } = useAnnotationStrokeColor();

  const pageRef = useRef(page);
  pageRef.current = page;

  const draftsRef = useRef(draftShapes);
  useEffect(() => {
    draftsRef.current = draftShapes;
  }, [draftShapes]);

  const colorRef = useRef(strokeColor);
  useEffect(() => {
    colorRef.current = strokeColor;
  }, [strokeColor]);

  const activePointerRef = useRef<number | null>(null);
  // Space+drag is reserved for the parent container's pan gesture so the user
  // can reposition the zoomed media without first deselecting the tool.
  const isSpacePressedRef = useSpaceKeyState();

  const isEnabled = isEnabledTool(tool, enabledTools);

  const normalize = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      return {
        x: clamp01((clientX - rect.left) / rect.width),
        y: clamp01((clientY - rect.top) / rect.height),
      };
    },
    [svgRef],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      if (!isEnabledTool(tool, enabledTools)) return;

      if (e.button !== 0) return;
      if (isSpacePressedRef.current) return;
      const p = normalize(e.clientX, e.clientY);
      if (!p) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      activePointerRef.current = e.pointerId;

      const seed = seedShape(tool, p);
      if (!seed) return;
      const draft: DraftShape =
        pageRef.current !== undefined
          ? { shape: seed, color: colorRef.current, page: pageRef.current }
          : { shape: seed, color: colorRef.current };
      setDraftShapes([...draftsRef.current, draft]);
    },
    [tool, enabledTools, normalize, setDraftShapes, isSpacePressedRef],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      const p = normalize(e.clientX, e.clientY);
      if (!p) return;
      const drafts = draftsRef.current;
      if (drafts.length === 0) return;
      const active = drafts[drafts.length - 1];
      const updatedShape = updateShape(active.shape, p);
      if (!updatedShape) return;
      setDraftShapes([...drafts.slice(0, -1), { ...active, shape: updatedShape }]);
    },
    [normalize, setDraftShapes],
  );

  const endPointer = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (activePointerRef.current !== e.pointerId) return;
    activePointerRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer may already be released (e.g. cancel fired before up).
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (draftsRef.current.length === 0) return;
      cancelDraft();
      activePointerRef.current = null;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cancelDraft]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
    isActive: isEnabled,
  };
}

function seedShape(tool: DrawingTool, p: { x: number; y: number }): AnnotationShape | null {
  switch (tool) {
    case 'pen':
      return { type: 'pen', points: [p] };
    case 'circle':
      return { type: 'circle', rect: { x: p.x, y: p.y, w: 0, h: 0 } };
    case 'rectangle':
      return { type: 'rectangle', rect: { x: p.x, y: p.y, w: 0, h: 0 } };
    case 'arrow':
      return { type: 'arrow', start: p, end: p };
  }
}

function updateShape(shape: AnnotationShape, p: { x: number; y: number }): AnnotationShape | null {
  switch (shape.type) {
    case 'pen': {
      const last = shape.points[shape.points.length - 1];
      if (distanceSq(last, p) < SAMPLE_DIST_SQ) return null;
      return { type: 'pen', points: [...shape.points, p] };
    }
    case 'circle':
    case 'rectangle':
      return {
        type: shape.type,
        rect: {
          x: shape.rect.x,
          y: shape.rect.y,
          w: p.x - shape.rect.x,
          h: p.y - shape.rect.y,
        },
      };
    case 'arrow':
      return { type: 'arrow', start: shape.start, end: p };
    default:
      // Text-highlight isn't drawable via this hook.
      return null;
  }
}

function isEnabledTool(
  tool: AnnotationTool | null,
  enabled: ReadonlyArray<DrawingTool>,
): tool is DrawingTool {
  return tool !== null && (enabled as ReadonlyArray<string>).includes(tool);
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function distanceSq(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
